import type { ReactNode } from 'react';
import { UploadCloud } from 'lucide-react';
import type { PaigeUploadProgress } from './paige.global';

/**
 * The one line the workbench shows while chosen files go up into the job: n of m, and the names
 * Paige will not read (a folder's spreadsheets and shortcuts), so the operator knows what is
 * happening between the picker closing and the pages being read (IMPLEMENTATION.md row 22).
 */
export function UploadProgress({ progress }: { progress: PaigeUploadProgress }): ReactNode {
  const skipped = progress.skipped ?? [];
  const unreadable = progress.unreadable ?? [];
  return (
    <div className="paige-upload-progress" role="status" aria-live="polite">
      <UploadCloud size={15} aria-hidden="true" />
      <span className="paige-upload-progress__count">
        {progress.sent >= progress.total
          ? `${progress.total.toLocaleString()} ${progress.total === 1 ? 'file' : 'files'} sent; Paige is reading them`
          : `Uploading ${progress.sent.toLocaleString()} of ${progress.total.toLocaleString()}`}
      </span>
      {skipped.length > 0 && (
        <span className="paige-upload-progress__skipped" title={skipped.join('\n')}>
          {skippedLine(skipped)}
        </span>
      )}
      {unreadable.length > 0 && (
        <span className="paige-upload-progress__skipped" title={unreadable.join('\n')}>
          {unreadableLine(unreadable)}
        </span>
      )}
    </div>
  );
}

/** "Skipped a.xlsx, b.txt" with the rest counted, so the line stays one line. */
export function skippedLine(skipped: string[]): string {
  return `Skipped ${namesOf(skipped)} (not PDFs or images)`;
}

/**
 * The ones Paige took in and could not read. Their kind was right and the rest of the import went
 * through without them (ADR-0012), so the wording has to say something different from
 * <see>skippedLine</see>: these are worth sending again.
 */
export function unreadableLine(unreadable: string[]): string {
  return `Could not read ${namesOf(unreadable)}; the rest went in`;
}

function namesOf(paths: string[]): string {
  const names = paths.slice(0, 3).map((path) => path.split('/').at(-1) ?? path);
  const more = paths.length - names.length;
  return `${names.join(', ')}${more > 0 ? ` and ${more.toLocaleString()} more` : ''}`;
}
